import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

const MediaPlayer = ({ mediaUrl, mediaType, isLocalFile, token, id, isCover = false }) => {
  const videoRef = useRef(null);

  // Construimos la URL con el token como query param. 
  // Esto permite que el navegador gestione el streaming (Range Requests).
  const getAuthenticatedUrl = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const endpoint = isCover
      ? `/api/user/cover-media/${id}`
      : `/api/user/download-content/${id}`;
    
    return `${baseUrl}${endpoint}?token=${token}`;
  };

  const finalSrc = isLocalFile ? getAuthenticatedUrl() : mediaUrl;

  useEffect(() => {
  const el = videoRef.current;
  if (!el) return;

  const handlePlay = () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const audioCtx = new AudioContext();
    const silence = audioCtx.createBuffer(1, 1, 22050);
    const source = audioCtx.createBufferSource();
    source.buffer = silence;
    source.connect(audioCtx.destination);
    source.start();

    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing';
    }
  };

  el.addEventListener('play', handlePlay);
  return () => el.removeEventListener('play', handlePlay);
}, [id]);

  // Forzar la recarga del elemento video cuando cambie el ID
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [id, finalSrc]);

  useEffect(() => {
  if ('mediaSession' in navigator && videoRef.current) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: 'Edeli Store',
      artist: '',
      album: '',
    });

    navigator.mediaSession.setActionHandler('play', () => {
      videoRef.current?.play();
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      videoRef.current?.pause();
    });

    navigator.mediaSession.setActionHandler('seekbackward', (details) => {
      if (videoRef.current) videoRef.current.currentTime -= details.seekOffset || 10;
    });

    navigator.mediaSession.setActionHandler('seekforward', (details) => {
      if (videoRef.current) videoRef.current.currentTime += details.seekOffset || 10;
    });
  }
}, [id]);

  return (
    <MediaPlayerContainer>
      {finalSrc ? (
        mediaType === 'video' ? (
          <MediaElement
            ref={videoRef}
            key={id}
            controls
            playsInline
            preload="none"
            crossOrigin="anonymous"
          >
            <source src={finalSrc} type="video/mp4" />
            Tu navegador no soporta la reproducción de este archivo.
          </MediaElement>
        ) : (
          <audio
            ref={videoRef}
            key={id}
            controls
            preload="none"
            crossOrigin="anonymous"
            style={{ width: '100%' }}
          >
            <source src={finalSrc} type="audio/mpeg" />
            Tu navegador no soporta la reproducción de este archivo.
          </audio>
        )
      ) : (
        <LoadingPlaceholder>
          <Spinner />
        </LoadingPlaceholder>
      )}
    </MediaPlayerContainer>
  );
};

const MediaPlayerContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MediaElement = styled.video`
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: contain; /* Mantiene la proporción del video sin estirarlo */
`;

const LoadingPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    100% { transform: rotate(360deg); }
  }
`;

export default MediaPlayer;